/**
 * Streaming POST Client.
 *
 * Instead of a long-lived EventSource (GET) (traditional SSE), this client POSTs a message
 * to the server and reads the streamed SSE response from the same HTTP
 * connection.  Connections last seconds (the duration of one handler
 * response), not minutes/hours, which avoids corporate-proxy buffering.
 *
 * Implements the same interface as SocketClient.js (onOpen, onMessage,
 * onClose, onError, send, sendObj, connect, close, removeListeners).
 */

import { handleEncryptionAccessDisabled } from "~vue/api/encryptionAccess";
import { refreshCsrfToken } from "~vue/utils/csrf";
import { logError } from "~vue/utils/logUtils";
import { CORRELATION_ID_HEADER, generateRequestId, REQUEST_ID_HEADER } from "~vue/utils/requestId";
import { reportSessionEnded, SESSION_ENDED_ERROR_CAUSE, sessionEndedCodeFrom, wasGloballyHandled403 } from "~vue/utils/sessionEnded";
import { isAlbWafBlock, WAF_ERROR_CAUSE } from "~vue/utils/wafDetection";

import { getCookie } from "../utils";

const DEFAULT_RECONNECT_INTERVAL = 2000;
const DEFAULT_STREAM_TIMEOUT = 20000;

interface StreamingPostClientOptions {
    reconnectEnabled?: boolean;
    reconnectInterval?: number;
    streamTimeout?: number;
}

interface MessageEvent {
    data: string;
    type: string;
}

interface CloseEvent {
    wasClean: boolean;
}

export interface RealtimeClient {
    onOpen: (() => void) | null;
    onMessage: ((event: MessageEvent) => void) | null;
    onClose: ((event: CloseEvent) => void) | null;
    onError: ((error: unknown) => void) | null;
    isConnected: boolean;
    nextRequestId: string | null;
    nextCorrelationId: string | null;
    connect(): void;
    reconnect(): void;
    close(): void;
    send(message: string): void | Promise<void>;
    sendObj(data: Record<string, unknown>): void | Promise<void>;
    removeListeners(): void;
    abortActiveRequest(): void;
}

export default class StreamingPostClient implements RealtimeClient {
    postUrl: string;
    options: StreamingPostClientOptions;

    onOpen: (() => void) | null = null;
    onMessage: ((event: MessageEvent) => void) | null = null;
    onClose: ((event: CloseEvent) => void) | null = null;
    onError: ((error: unknown) => void) | null = null;

    isConnected = false;
    nextRequestId: string | null = null;
    nextCorrelationId: string | null = null;

    private _abortController: AbortController | null = null;
    private _streamTimeout: number;

    constructor(url: string, options?: StreamingPostClientOptions) {
        if (!url || typeof url !== "string") {
            throw new Error("StreamingPostClient requires a valid URL string");
        }

        this.postUrl = url;
        this.options = { reconnectEnabled: false, reconnectInterval: DEFAULT_RECONNECT_INTERVAL, ...options };
        this._streamTimeout = options?.streamTimeout ?? DEFAULT_STREAM_TIMEOUT;
    }

    /**
     * No persistent connection to establish. Fire onOpen via setTimeout
     * so callers can set callbacks after calling connect() (microtask ordering).
     */
    connect(): void {
        this.isConnected = true;
        setTimeout(() => {
            if (typeof this.onOpen === "function") {
                this.onOpen();
            }
        }, 0);
    }

    /**
     * No-op — there is no persistent connection to re-establish.
     */
    reconnect(): void {
        // Intentional no-op
    }

    /**
     * Abort any in-flight streaming request and fire onClose.
     */
    close(): void {
        this._cleanupActiveRequest();
        this.isConnected = false;
        if (typeof this.onClose === "function") {
            this.onClose({ wasClean: true });
        }
    }

    /**
     * POST the message to the streaming endpoint and read the SSE response.
     *
     * If a previous request is still streaming it is aborted first.
     */
    async send(message: string): Promise<void> {
        if (!message || typeof message !== "string") {
            const error = new Error("StreamingPostClient.send() requires a string message");
            this._emitError(error);
            throw error;
        }

        // Abort any previous in-flight request
        this._cleanupActiveRequest();

        const csrfToken = getCookie("csrftoken");
        const requestId = this.nextRequestId ?? generateRequestId();
        this.nextRequestId = null;
        const correlationId = this.nextCorrelationId;
        this.nextCorrelationId = null;
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            [REQUEST_ID_HEADER]: requestId,
        };
        if (correlationId) {
            headers[CORRELATION_ID_HEADER] = correlationId;
        }

        if (csrfToken) {
            headers["X-CSRFToken"] = csrfToken;
        }

        this._abortController = new AbortController();
        // Capture reference so we can detect if a newer send() has replaced it
        const controller = this._abortController;

        // Native fetch() is required here (instead of useFetch) because we need
        // raw ReadableStream access via response.body.getReader() for SSE streaming.
        let response: Response;
        try {
            response = await fetch(this.postUrl, {
                method: "POST",
                headers,
                body: message,
                credentials: "same-origin",
                signal: controller.signal,
            });
        } catch (error: unknown) {
            if (this._isStaleOrAborted(error, controller)) {
                return;
            }
            this._emitError(error);
            throw error;
        }

        // Embedded webviews (MS Teams) can hold a stale csrftoken cookie that 403s
        // every POST — fetch a server-issued token and retry exactly once. Stands down
        // when the 403 carries a session-ended code, because then the global fetch
        // wrapper already retried and (on failure) reported the dialog; a further
        // attempt could land the write after the user was told it failed.
        if (response.status === 403 && !(await wasGloballyHandled403(response))) {
            const freshToken = await this._refreshCsrfToken(controller);
            if (this._abortController !== controller || controller.signal.aborted) {
                // A newer send() superseded this request while the token refresh
                // was in flight — drop it silently instead of surfacing its 403.
                return;
            }
            if (freshToken) {
                headers["X-CSRFToken"] = freshToken;
                try {
                    response = await fetch(this.postUrl, {
                        method: "POST",
                        headers,
                        body: message,
                        credentials: "same-origin",
                        signal: controller.signal,
                    });
                } catch (error: unknown) {
                    if (this._isStaleOrAborted(error, controller)) {
                        return;
                    }
                    this._emitError(error);
                    throw error;
                }
            }
        }

        await this._handleHttpError(response);

        // Read the streaming response body
        await this._readStream(response, controller);
    }

    /**
     * Convenience wrapper: send an object as JSON.
     */
    sendObj(data: Record<string, unknown>): Promise<void> {
        if (!data || typeof data !== "object") {
            const error = new Error("StreamingPostClient.sendObj() requires an object");
            this._emitError(error);
            return Promise.reject(error);
        }
        return this.send(JSON.stringify(data));
    }

    /**
     * Abort the in-flight streaming request without closing the client.
     * Used by "stop generating" to cancel the backend handler mid-stream.
     */
    abortActiveRequest(): void {
        this._cleanupActiveRequest();
    }

    /**
     * Remove all callbacks and close.
     */
    removeListeners(): void {
        this.onOpen = null;
        this.onMessage = null;
        this.onClose = null;
        this.onError = null;
        this.close();
    }

    // ── Private helpers ──────────────────────────────────────────────

    private _emitError(error: unknown): void {
        if (typeof this.onError === "function") {
            this.onError(error);
        }
    }

    /**
     * Fetch a fresh CSRF token, aborting alongside the active streaming request.
     * Returns null on any failure so the caller falls through to normal 403 handling.
     */
    private _refreshCsrfToken(controller: AbortController): Promise<string | null> {
        return refreshCsrfToken(controller.signal);
    }

    private async _handleHttpError(response: Response): Promise<void> {
        handleEncryptionAccessDisabled(response);
        if (response.ok) {
            return;
        }
        let errorData: Record<string, unknown> = {};
        try {
            errorData = await response.json();
        } catch {
            errorData = { error: response.statusText };
        }
        const err = new Error((errorData.error as string) || `HTTP ${response.status}: ${response.statusText}`);
        (err as unknown as Record<string, unknown>).status = response.status;
        (err as unknown as Record<string, unknown>).data = errorData;
        // A 403 that survives the CSRF refresh-and-retry-once logic in send() can still
        // be a genuine WAF/ALB security block — see isAlbWafBlock for the detection rule.
        if (isAlbWafBlock(response)) {
            (err as unknown as Record<string, unknown>).cause = WAF_ERROR_CAUSE;
        }
        // Or it can be a session the refresh could not save, in which case the user needs the
        // dialog rather than an inline "send failed" they can only retry into the same 403
        // (BUG-947). Reported in addition to the error below, not instead of it: the chat UI
        // still has to unwind its pending message.
        const sessionEndedCode = sessionEndedCodeFrom(errorData);
        if (sessionEndedCode) {
            reportSessionEnded(sessionEndedCode, (errorData.login_url as string) ?? null);
            (err as unknown as Record<string, unknown>).cause = SESSION_ENDED_ERROR_CAUSE;
        }
        this._emitError(err);
        throw err;
    }

    private _cleanupActiveRequest(): void {
        if (this._abortController) {
            this._abortController.abort();
            this._abortController = null;
        }
    }

    /**
     * Parse a single SSE frame and return the payload string to dispatch,
     * or `null` if the frame should be skipped.
     */
    private _parseSseFrame(frame: string): string | null {
        if (!frame.trim()) {
            return null;
        }

        // SSE comment lines start with ':'  (keepalives, etc.)
        if (frame.startsWith(":")) {
            return null;
        }

        // Extract data from "data: {...}" lines
        const dataLine = frame.split("\n").find((line) => line.startsWith("data: "));
        if (!dataLine) {
            return null;
        }

        const payload = dataLine.slice("data: ".length);

        // Skip the stream_complete sentinel
        try {
            const parsed = JSON.parse(payload);
            if (parsed.type === "stream_complete") {
                return null;
            }
        } catch {
            // Not JSON — still forward to onMessage
        }

        return payload;
    }

    /**
     * Returns true if the error is an AbortError or the controller is stale
     * (a newer send() has replaced it), meaning the error should be silently ignored.
     */
    private _isStaleOrAborted(error: unknown, controller: AbortController): boolean {
        if (error instanceof DOMException && error.name === "AbortError") {
            return true;
        }
        return this._abortController !== controller;
    }

    /**
     * Read a streaming `text/event-stream` response using ReadableStream,
     * parse SSE frames, and dispatch to onMessage.
     */
    private async _readStream(response: Response, controller: AbortController): Promise<void> {
        const reader = response.body?.getReader();
        if (!reader) {
            return;
        }

        const decoder = new TextDecoder();
        let buffer = "";
        let receivedStreamData = false;

        const streamTimer = setTimeout(() => {
            if (!receivedStreamData) {
                logError(new Error("SSE stream timeout: no data frame received within time limit"), {
                    context: { streaming: { url: this.postUrl, timeoutMs: this._streamTimeout } },
                    tags: { error_type: "sse_stream_timeout" },
                });
            }
        }, this._streamTimeout);

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });

                // Any bytes from the stream (including keepalive comments) prove the connection is alive
                if (!receivedStreamData) {
                    receivedStreamData = true;
                    clearTimeout(streamTimer);
                }

                // Split on double-newline (SSE frame boundary)
                const frames = buffer.split("\n\n");
                // Last element is incomplete — keep it in the buffer
                buffer = frames.pop() ?? "";

                for (const frame of frames) {
                    const payload = this._parseSseFrame(frame);
                    if (payload !== null) {
                        this.onMessage?.({ data: payload, type: "message" });
                    }
                }
            }
        } catch (error: unknown) {
            if (!this._isStaleOrAborted(error, controller)) {
                this._emitError(error);
            }
        } finally {
            clearTimeout(streamTimer);
            reader.releaseLock();
        }
    }
}
